import React, { useEffect, useRef } from "react";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ── Theme tokens ──────────────────────────────────────────────────────────────
const T = {
  navy: "#A81026",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
  slate: "#5B677A",
  bg: "#F5F8FC",
  card: "#FFFFFF",
  line: "#DCE5EF",
  ink: "#1D2733",
};

// ── 5S style colour tokens ────────────────────────────────────────────────────
const STYLE_COLORS = {
  Signaling: { color: "#3b5ec0", bg: "#f0f4ff", fillStart: "#1e3a8a", fillEnd: "#3b82f6" },
  Stimulating: { color: "#16a34a", bg: "#f0fdf4", fillStart: "#14532d", fillEnd: "#22c55e" },
  Suggesting: { color: "#d9790a", bg: "#fffbeb", fillStart: "#b45309", fillEnd: "#f59e0b" },
  Sharing_Sanctioning: { color: "#7c3aed", bg: "#faf5ff", fillStart: "#4c1d95", fillEnd: "#a78bfa" },
  Total_Adaptability: { color: "#6d28d9", bg: "#f8f0ff", fillStart: "#4c1d95", fillEnd: "#c4b5fd" },
};

// ── Adaptability tier helper ──────────────────────────────────────────────────
const ADAPT_ROWS = [
  {
    tier: "low",
    pill: "Low (Below 4)",
    pc: "#a90c2c", pb: "#fdf0f2", border: "#f5c6cf",
    desc: "You currently rely on a limited range of leadership styles and may find it challenging to adjust to different situations. Building awareness of alternative approaches and practising flexibility will significantly strengthen your effectiveness.",
  },
  {
    tier: "midlow",
    pill: "Mid to Low (5–10)",
    pc: "#92400e", pb: "#fffbeb", border: "#fde68a",
    desc: "You show some adaptability but often default to familiar styles. Expanding your range and intentionally applying different approaches will improve your impact across varied situations.",
  },
  {
    tier: "midhigh",
    pill: "Mid to High (11–15)",
    pc: "#166534", pb: "#f0fdf4", border: "#bbf7d0",
    desc: "You demonstrate good adaptability and can adjust your style in many situations. With continued practice, you can become even more fluid and intentional in your approach.",
  },
  {
    tier: "high",
    pill: "High (16 and above)",
    pc: "#1f2e54", pb: "#f0f3fb", border: "#c7d2f0",
    desc: "You are highly adaptable — you read situations well and shift your leadership style effectively to meet the needs of others.",
  },
];
const getAdaptTier = (s) =>
  s >= 16 ? "high" : s >= 11 ? "midhigh" : s >= 5 ? "midlow" : "low";

// ── Style definition content ──────────────────────────────────────────────────
const STYLE_DEFS = [
  {
    key: "signaling",
    dominantKey: "Signaling",
    color: STYLE_COLORS.Signaling.color,
    bg: STYLE_COLORS.Signaling.bg,
    border: "#bfdbfe",
    emoji: "📡",
    label: "Signaling — Leadership Style",
    archetype: "The Directing Leader",
    tagline: "You lead through clarity and structure.",
    conceptual:
      "Leaders who score high on Signaling are highly attuned to context and environment. They read the room, pick up on unspoken cues, and communicate through visible actions, symbols, and presence as much as through words. Their leadership is characterised by deliberate messaging and purposeful visibility.",
    strengths: [
      "You bring order and clarity to ambiguous situations.",
      "People feel secure and focused under your guidance.",
      "You excel in high-stakes, time-sensitive, or new-team environments.",
    ],
    growth:
      "As your team members grow in competence and confidence, experiment with asking their opinion before giving direction. Moving up the leadership ladder — even occasionally — can unlock greater ownership and initiative in your people.",
    quote: "Structure is a gift — but autonomy is the next level.",
    cognitive: [
      "Process their environment holistically, noticing patterns, energy shifts, and unspoken dynamics before others articulate them.",
      "Prefer to lead by example and demonstration, believing actions carry more weight than instructions.",
      "Tend to think in frameworks, naturally translating complex ideas into clear signals and cues for their teams.",
    ],
    behavior: [
      "Communicate priorities through visible behaviours — how they spend time, who they recognise, what they address publicly.",
      "Set the cultural tone of a team through consistent, deliberate non-verbal and symbolic cues.",
      "In meetings, listen intently before speaking; when they do speak, their words carry disproportionate weight.",
    ],
  },
  {
    key: "stimulating",
    dominantKey: "Stimulating",
    color: STYLE_COLORS.Stimulating.color,
    bg: STYLE_COLORS.Stimulating.bg,
    border: "#bbf7d0",
    emoji: "⚡",
    label: "Stimulating — Leadership Style",
    archetype: "The Motivating Leader",
    tagline: "You lead through encouragement and energy.",
    conceptual:
      "Leaders with a Stimulating style naturally blend direction with motivation. They don't just tell people what to do — they spark willingness to act, ask stretching questions, build confidence, and get people to step up.",
    strengths: [
      "You build team morale and a sense of purpose.",
      "You help hesitant or developing team members find their footing.",
      "You balance structure with emotional connection.",
    ],
    growth:
      "Watch for moments when your encouragement might substitute for honest feedback. The most effective Stimulating leaders know when to shift from cheerleading to coaching — and when to challenge the team to go further independently.",
    quote: "You light the fire — now let them tend it.",
    cognitive: [
      "Naturally drawn to complexity, challenge, and unexplored possibility — energised by problems that others find daunting.",
      "Think expansively, connecting seemingly unrelated ideas to surface new approaches.",
      "Process information quickly and enjoy dialogue as a mode of thinking — they refine ideas through debate.",
    ],
    behavior: [
      "Regularly ask provocative or stretch questions designed to expand thinking.",
      "Create forums where ideas are challenged respectfully and bold thinking is rewarded.",
      "Attract high-potential talent because they create environments where growth is expected.",
    ],
  },
  {
    key: "suggesting",
    dominantKey: "Suggesting",
    color: STYLE_COLORS.Suggesting.color,
    bg: STYLE_COLORS.Suggesting.bg,
    border: "#fde68a",
    emoji: "💡",
    label: "Suggesting — Leadership Style",
    archetype: "The Guiding Leader",
    tagline: "You lead through questions and co-creation.",
    conceptual:
      "Leaders with a Suggesting style naturally invite input before making decisions. They treat capable team members as thinking partners, creating space for ideas to emerge from the team rather than always originating from themselves.",
    strengths: [
      "You develop problem-solving skills in your team.",
      "You build buy-in by involving people in the process.",
      "You create psychologically safe environments for ideas and feedback.",
    ],
    growth:
      "Be mindful of situations where team members need firmer direction — especially in moments of uncertainty or crisis. Not every situation calls for collaborative exploration. Knowing when to step down the ladder and Signal clearly is equally important.",
    quote: "The best questions create the best answers — and the best teams.",
    cognitive: [
      "Prefer to influence through well-timed, well-framed recommendations rather than mandates.",
      "Strong analytical thinkers who invest time in understanding context before offering input.",
      "Patient communicators; allow ideas to develop through conversation, comfortable with ambiguity.",
    ],
    behavior: [
      "Frequently act as sounding boards; colleagues often seek them out for perspective.",
      "Frame recommendations indirectly, inviting others to arrive at conclusions.",
      "Build influence quietly over time through the quality of decisions teams make after consulting them.",
    ],
  },
  {
    key: "sharing_sanctioning",
    dominantKey: "Sharing & Sanctioning",
    color: STYLE_COLORS.Sharing_Sanctioning.color,
    bg: STYLE_COLORS.Sharing_Sanctioning.bg,
    border: "#e9d5ff",
    emoji: "🤝",
    label: "Sharing & Sanctioning — Leadership Style",
    archetype: "The Empowering Leader",
    tagline: "You lead through trust, partnership, and full delegation.",
    conceptual:
      "Leaders with a Sharing & Sanctioning style are builders of shared ownership and collective accountability. They are highly effective at creating alignment, distributing authority, and ensuring that standards and expectations are held clearly across the team.",
    strengths: [
      "You build highly autonomous, self-directed teams.",
      "You develop future leaders by giving them real authority.",
      "Your team members grow faster under your leadership.",
    ],
    growth:
      "Be intentional about who you delegate to — and when. Sharing & Sanctioning works best with capable, confident individuals. With newer or struggling team members, stepping down the ladder to Signal or Stimulate is not a weakness — it's exactly the right call.",
    quote: "The greatest leaders make themselves unnecessary — by making others extraordinary.",
    cognitive: [
      "Value collective wisdom and shared decision-making; outcomes improve when more perspectives are incorporated.",
      "Strong at designing processes and rituals that embed shared norms across the team.",
      "Highly attuned to fairness, equity, and consistency.",
    ],
    behavior: [
      "Actively distribute ownership, ensuring team members understand their authority and responsibilities.",
      "Create transparent frameworks for decision-making so teams understand how and why choices are made.",
      "Build highly cohesive teams by investing in shared values, rituals, and common language.",
    ],
  },
];

/**
 * Hidden5SReport
 * ──────────────
 * Renders the 5S Leadership Style report offscreen, captures each .ss-page with
 * html2canvas, stitches into a multi-page A4 PDF via jsPDF, then saves it.
 *
 * Props:
 *   participantData  – null | { name, email, phone, submission_time, scores }
 *   onComplete       – () => void
 */
export default function Hidden5SReport({ participantData, scriptsReady, onComplete }) {
  const containerRef = useRef(null);
  const hasRun = useRef(false);

  // ── Load html2canvas + jsPDF once (works whether caller pre-loads or not) ───
  useEffect(() => {
    const load = (src, id) => {
      if (document.getElementById(id)) return;
      const s = document.createElement("script");
      s.id = id; s.src = src;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-5s-hidden");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", "jspdf-5s-hidden");
  }, []);

  // ── Generate PDF whenever participantData is set ────────────────────────────
  useEffect(() => {
    if (!participantData) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const generate = async () => {
      // Wait for scripts — with a 15s timeout escape hatch
      await new Promise((resolve, reject) => {
        if (window.html2canvas && window.jspdf?.jsPDF) { resolve(); return; }
        const start = Date.now();
        const check = () => {
          if (window.html2canvas && window.jspdf?.jsPDF) { resolve(); return; }
          if (Date.now() - start > 15000) { reject(new Error('PDF libraries failed to load within 15 seconds.')); return; }
          setTimeout(check, 150);
        };
        check();
      });

      // Give React one tick to finish rendering the hidden DOM
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise((r) => setTimeout(r, 400));

      const el = containerRef.current;
      if (!el) { hasRun.current = false; onComplete?.(); return; }

      try {
        const { jsPDF } = window.jspdf;
        const A4_W = 210;
        const A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".ss-page"));
        if (pageEls.length === 0) {
          console.error("Hidden5SReport: no .ss-page elements found in container");
          onComplete?.(); return;
        }

        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: pageEls[i].scrollWidth,
            height: pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX = 25.4 / 96;
          const naturalW = logicalW * MM_PER_PX;
          const naturalH = logicalH * MM_PER_PX;
          const scaleToW = A4_W / naturalW;
          const projectedH = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2; yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.name || "Report").replace(/\s+/g, "_");
        pdf.save(`5S_Report_${safeName}.pdf`);
      } catch (err) {
        console.error("5S PDF generation failed:", err);
        alert("PDF generation failed: " + err.message);
      } finally {
        hasRun.current = false;
        onComplete?.();
      }
    };

    generate();
  }, [participantData]);

  if (!participantData) return null;

  const { name, email, phone, submission_time, scores = {} } = participantData;

  const sig = scores.Signaling || 0;
  const stim = scores.Stimulating || 0;
  const sug = scores.Suggesting || 0;
  const shar = scores.Sharing_Sanctioning || 0;
  const adapt = scores.Total_Adaptability || 0;

  const total = (sig + stim + sug + shar) || 12;

  const sigPct = ((sig / total) * 100).toFixed(1) + "%";
  const stimPct = ((stim / total) * 100).toFixed(1) + "%";
  const sugPct = ((sug / total) * 100).toFixed(1) + "%";
  const sharPct = ((shar / total) * 100).toFixed(1) + "%";

  // Adaptability bar: scale -24..+24 → 0..100%
  const adaptBarPct = (((adapt + 24) / 48) * 100).toFixed(1) + "%";

  // Dominant style
  const styleKeyScores = [
    { key: "Signaling", val: sig, label: "Signaling" },
    { key: "Stimulating", val: stim, label: "Stimulating" },
    { key: "Suggesting", val: sug, label: "Suggesting" },
    { key: "Sharing_Sanctioning", val: shar, label: "Sharing & Sanctioning" },
  ];
  const dominantStyle = styleKeyScores.reduce((a, b) => (b.val > a.val ? b : a)).label;

  // Sorted highest-score-first — used only on the "Leadership Style Definitions" pages
  const STYLE_DEFS_SORTED = [...STYLE_DEFS].sort((a, b) => {
    const aVal = styleKeyScores.find((s) => s.label === a.dominantKey)?.val ?? -Infinity;
    const bVal = styleKeyScores.find((s) => s.label === b.dominantKey)?.val ?? -Infinity;
    return bVal - aVal;
  });

  const dateStr = submission_time
    ? new Date(submission_time).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  // ── Adaptability tier ────────────────────────────────────────────────────
  const currentTierKey = getAdaptTier(adapt);
  const currentTierRow = ADAPT_ROWS.find((r) => r.tier === currentTierKey);

  const tierLabelStyle = {
    fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
    padding: "5px 12px", borderRadius: "999px",
    background: currentTierRow.pb, color: currentTierRow.pc,
    border: `1px solid ${currentTierRow.border}`,
  };

  // ── Result & adaptability summary text ──────────────────────────────────
  const resultText =
    `Based on your responses, your primary leadership influence style is ${dominantStyle}. ` +
    (dominantStyle === "Signaling"
      ? "You lead through presence, action, and deliberate communication — your team reads your cues and takes direction from what you visibly prioritise and model."
      : dominantStyle === "Stimulating"
        ? "You lead by raising energy, ambition, and intellectual challenge — your natural mode is to energise and stretch the people around you toward greater performance."
        : dominantStyle === "Suggesting"
          ? "You lead through trusted insight and well-timed counsel — your influence is built on the quality of your perspective and your skill in guiding others toward sound conclusions."
          : "You lead through shared ownership and collective accountability — your strength is in aligning people around clear expectations and ensuring that standards are held consistently.");

  // ── renderStyleCard — highlights the dominant style ──────────────────────
  const renderStyleCard = (style) => {
    const isHighlighted = style.dominantKey === dominantStyle;

    return (
      <div
        key={style.key}
        style={{
          border: isHighlighted
            ? `2px solid ${style.color}`
            : "1px solid #d9e0e7",
          borderLeft: `5px solid ${style.color}`,
          borderRadius: "16px",
          padding: "1.25rem",
          background: isHighlighted ? style.bg : "#fff",
          marginBottom: "1.5rem",
          position: "relative",
        }}
      >
        {/* Highlighted badge */}
        {isHighlighted && (
          <div style={{
            position: "absolute", top: "1rem", right: "1rem",
            background: style.color, color: "#fff",
            fontSize: "10px", fontWeight: 800,
            padding: "3px 10px", borderRadius: "999px",
            textTransform: "uppercase", letterSpacing: ".06em",
          }}>
            ★ Your Style
          </div>
        )}

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "0.75rem" }}>
          <div style={{
            width: "40px", height: "40px", flexShrink: 0,
            borderRadius: "10px", background: "#fff",
            border: `1px solid ${style.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px",
          }}>
            {style.emoji}
          </div>
          <div>
            <div style={{ fontSize: "9px", fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: style.color, marginBottom: "2px" }}>
              {style.label}
            </div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#0d0e10", lineHeight: 1.15 }}>
              {style.archetype}
            </div>
            <div style={{ fontSize: "12px", fontStyle: "italic", color: "#4b5068", marginTop: "2px" }}>
              "{style.tagline}"
            </div>
          </div>
        </div>

        {/* Conceptual definition */}
        <p style={{ margin: "0 0 0.75rem", color: "#334155", fontSize: "0.9rem", lineHeight: 1.65, textAlign: "justify" }}>
          <strong>Conceptual Definition:</strong> {style.conceptual}
        </p>

        {/* Strengths + Growth edge side by side */}
        <div style={{ display: "flex", gap: "14px", marginBottom: "0.75rem" }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: "0 0 0.35rem", fontWeight: 700, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: ".07em", color: "#4b5068" }}>
              Strengths
            </p>
            {style.strengths.map((st, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "7px", marginBottom: "5px" }}>
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: style.color, flexShrink: 0, marginTop: "5px" }} />
                <span style={{ fontSize: "0.88rem", color: "#0d0e10", lineHeight: 1.5 }}>{st}</span>
              </div>
            ))}
          </div>
          <div style={{
            flex: 1, background: isHighlighted ? "#fff" : style.bg,
            border: `1px solid ${style.border}`,
            borderRadius: "10px", padding: "12px 14px",
          }}>
            <div style={{ fontSize: "9px", fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: style.color, marginBottom: "6px" }}>
              Growth Edge
            </div>
            <p style={{ fontSize: "0.88rem", color: "#0d0e10", lineHeight: 1.6, margin: 0, textAlign: "justify" }}>{style.growth}</p>
          </div>
        </div>

        {/* Cognitive + Behavioral bullets */}
        <p style={{ margin: "0 0 0.35rem", fontWeight: 700, fontSize: "0.85rem", color: "#0f2744" }}>Cognitive Characteristics</p>
        <ul style={{ margin: "0 0 0.75rem 0", paddingLeft: "1.25rem", fontSize: "0.85rem", color: "#475569" }}>
          {style.cognitive.map((pt, i) => <li key={i} style={{ marginBottom: "0.3rem" }}>{pt}</li>)}
        </ul>
        <p style={{ margin: "0 0 0.35rem", fontWeight: 700, fontSize: "0.85rem", color: "#0f2744" }}>Behavioural Indicators</p>
        <ul style={{ margin: "0 0 0.75rem 0", paddingLeft: "1.25rem", fontSize: "0.85rem", color: "#475569" }}>
          {style.behavior.map((pt, i) => <li key={i} style={{ marginBottom: "0.3rem" }}>{pt}</li>)}
        </ul>

        {/* Quote */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingTop: "12px", borderTop: "1px solid #e4e8f0" }}>
          <div style={{ width: "3px", flexShrink: 0, height: "30px", background: style.color, borderRadius: "999px" }} />
          <span style={{ fontSize: "12px", fontStyle: "italic", fontWeight: 600, color: "#4b5068", lineHeight: 1.5 }}>
            "{style.quote}"
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "0",
        left: "-9999px",
        width: "1100px",
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />

      <style>{`
        .ss-pdf-root {
          font-family: 'Satoshi', sans-serif;
          background: linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
          color: #1f2937;
          line-height: 1.5;
        }
        .ss-pdf-root .report { max-width: 1100px; margin: 0 auto; padding: 2rem 1rem 4rem; }
        .ss-pdf-root .ss-page {
          background: #ffffff; border: 1px solid #ffffff; border-radius: 28px;
          box-shadow: 0 18px 40px rgba(14,39,68,.10); overflow: hidden; margin-bottom: 2rem;
        }

        /* Cover */
        .ss-pdf-root .cover { min-height:1556px; position:relative; color:white; }
        .ss-pdf-root .cover-inner { padding:3.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:1556px; }
        .ss-pdf-root .brand { display:flex; align-items:center; justify-content:space-between; gap:1rem; position:relative; z-index: 2; }
        .ss-pdf-root .badge { border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1); padding:.5rem 1rem; border-radius:999px; font-size:0.76rem; }

        .ss-pdf-root .logo-panel {
          position: absolute; top: 0; right: 0;
          width: 500px; height: 250px;
          display: flex; align-items: center; justify-content: center; z-index: 1;
        }
        .ss-pdf-root .logo-panel-inner {
          display: flex; flex-direction: column; align-items: center;
          position: relative; z-index: 2; padding-left: 60px; padding-bottom: 25px;
        }
        .ss-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

        .ss-pdf-root .cover-title h2 {margin:0 0 1rem; font-size:3.4rem;line-height:.95; color:#FFFFFF;white-space:nowrap; max-width:none; border-left: 6px solid #FFFFFF !important; padding-left: 1rem;}
        .ss-pdf-root p { text-align: justify; }
        .ss-pdf-root .cover-title p  { font-size:1.2rem; color:rgba(255,255,255,.82); max-width:100%; text-align:justify; }
        .ss-pdf-root .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem; }
        .ss-pdf-root .meta { background:rgba(255,255,255,.1); font-size:1.2rem; border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem; }
        .ss-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
        .ss-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
        .ss-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem; }
        .ss-pdf-root .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
        .ss-pdf-root .footer-note { display:flex; justify-content:space-between; gap:1rem; color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:1rem; }

        /* Inner pages */
        .ss-pdf-root .page-inner { padding: 2.5rem; }
        .ss-pdf-root .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin-bottom:2rem; }
        .ss-pdf-root .section-kicker { color:#A81026; font-size:0.8rem; text-transform:uppercase; letter-spacing:.12em; font-weight:700; }
        .ss-pdf-root .section-title { margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; border-left: 5px solid #C4163A; padding-left: .75rem; }
        .ss-pdf-root .summary-grid { display:grid; grid-template-columns:1fr; gap:1rem; }
        .ss-pdf-root .card { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid #d9e0e7; border-radius:20px; padding:1.5rem; }
        .ss-pdf-root .card h3 { margin:0 0 .75rem; font-size:1.2rem; color:#0f2744; }
        .ss-pdf-root .lead { color:#334155; font-size:1rem; margin:0; text-align:justify; }
        .ss-pdf-root .pill { display:inline-flex; align-items:center; gap:.45rem; padding:.5rem .85rem; border-radius:999px; font-size:0.8rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; }
        .ss-pdf-root .pill.red { background:#fef2f2; color:#A81026; }

        /* Bars */
        .ss-pdf-root .bars { display:grid; gap:1.2rem; margin-top:1rem; }
        .ss-pdf-root .bar-row { display:grid; grid-template-columns:180px 1fr 60px; gap:1rem; align-items:center; }
        .ss-pdf-root .bar-label { font-size:0.95rem; font-weight:700; color:#0f2744; }
        .ss-pdf-root .track { height:24px; background:#e9eef3; border-radius:999px; overflow:hidden; position:relative; }
        .ss-pdf-root .fill { height:100%; border-radius:999px; position:relative; }
        .ss-pdf-root .fill.sig  { background:linear-gradient(90deg,#1e3a8a,#3b82f6); }
        .ss-pdf-root .fill.stim { background:linear-gradient(90deg,#14532d,#22c55e); }
        .ss-pdf-root .fill.sug  { background:linear-gradient(90deg,#b45309,#f59e0b); }
        .ss-pdf-root .fill.shar { background:linear-gradient(90deg,#4c1d95,#a78bfa); }
        .ss-pdf-root .fill.adpt { background:linear-gradient(90deg,#4c1d95,#c4b5fd); }
        .ss-pdf-root .score-box { text-align:right; font-variant-numeric:tabular-nums; }
        .ss-pdf-root .score-box strong { display:block; color:#0f2744; font-size:1.1rem; }

        /* Callout */
        .ss-pdf-root .callout { background:linear-gradient(135deg,#fff7f7,#fbeaea); border:1px solid #d9e0e7; border-radius:18px; padding:1.25rem 1.5rem; color:#334155; }

        /* Back cover */
        .ss-pdf-root .back-cover { min-height:1556px; color:white; }
        .ss-pdf-root .back-cover .page-inner { min-height:1556px; display:flex; flex-direction:column; justify-content:space-between; }
        .ss-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .ss-pdf-root .closing p  { text-align:justify; max-width:100%; color:rgba(255,255,255,.82); font-size:1.2rem; }
        .ss-pdf-root .contact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem; }
        .ss-pdf-root .contact { background:rgba(168,16,38,.1); border:1px solid rgba(255,255,255,.16); border-radius:16px; padding:.9rem; }
        .ss-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
        .ss-pdf-root .contact strong { font-size:0.92rem; }
        .ss-pdf-root .small { font-size:0.73rem; color:#5B677A; }
        .ss-pdf-root .light { color:rgba(255,255,255,.66); }
      `}</style>

      <div className="ss-pdf-root">
        <main className="report">

          {/* ── PAGE 1 — COVER ────────────────────────────────────────────── */}
          <section className="ss-page cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
              minHeight: "1556px", flexDirection: "column",
            }}>
              <div className="logo-panel">
                <svg width="100%" height="100%" viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none">
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="cover-inner">
                <div className="brand">
                  <div className="badge">Confidential Individual Report</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: "relative", zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>5S Leadership Style Assessment</h2>
                    <p>
                      This comprehensive assessment report presents an analysis of your leadership influence preferences using the 5S framework, helping identify how you most naturally lead, motivate, and guide others — and how adaptable your style is across different contexts and situations.
                    </p>
                  </div>

                  <div className="cover-grid">
                    <div className="meta"><span>Participant</span><strong>{name || "—"}</strong></div>
                    <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                    <div className="meta"><span>Primary Style</span><strong>{dominantStyle}</strong></div>
                  </div>

                  <div className="cover-badges">
                    {["Assessment Summary", "5S Distribution", "Adaptability Score", "Style Definitions"].map((b) => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="footer-note" style={{ position: "relative", zIndex: 2 }}>
                  <div>Based on the 5S Leadership Influence Style Survey framework.</div>
                  <div>12 questions distributed across four leadership modes.</div>
                </div>
              </div>
            </div>
          </section>

          {/* ── PAGE 2 — ASSESSMENT SUMMARY ───────────────────────────────── */}
          <section className="ss-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Assessment Summary</h2>
                </div>
                <div className="pill red">Dominant Style: {dominantStyle}</div>
              </div>

              <div className="summary-grid">
                <div className="card">
                  <h3>Understanding Your Leadership Style</h3>
                  <p className="lead">
                    While each leader has a primary mode through which they most naturally influence others, effective leadership requires the ability to flex across all four styles. The 5S framework identifies not just your dominant mode but your overall adaptability — the degree to which you adjust your approach based on context, audience, and situation.
                  </p>
                  <p className="lead" style={{ marginTop: "1rem" }}>
                    The survey you completed was designed to surface your predominant leadership influence preference. These scores reveal your natural style, providing actionable insight into how you build rapport, motivate others, and can deepen your effectiveness by recognising and matching the preferred styles of the people you lead.
                  </p>
                </div>

                <div className="card">
                  <h3>Why Style Awareness Matters</h3>
                  <p className="lead">
                    Research consistently shows that leaders who understand their own influence style — and can consciously adapt it — achieve stronger engagement, better communication outcomes, and higher team performance. Style awareness is not about changing who you are; it is about expanding your range so you can meet people where they are and lead more effectively in diverse contexts.
                  </p>
                </div>

                <div className="card">
                  <h3>Your 5S Distribution</h3>
                  <div className="bars">
                    <div className="bar-row">
                      <div className="bar-label">Signaling</div>
                      <div className="track"><div className="fill sig" style={{ width: sigPct }} /></div>
                      <div className="score-box"><strong>{sig}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Stimulating</div>
                      <div className="track"><div className="fill stim" style={{ width: stimPct }} /></div>
                      <div className="score-box"><strong>{stim}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Suggesting</div>
                      <div className="track"><div className="fill sug" style={{ width: sugPct }} /></div>
                      <div className="score-box"><strong>{sug}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Sharing &amp; Sanctioning</div>
                      <div className="track"><div className="fill shar" style={{ width: sharPct }} /></div>
                      <div className="score-box"><strong>{shar}</strong></div>
                    </div>
                    <div className="bar-row" style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px dashed #e2e8f0" }}>
                      <div className="bar-label" style={{ fontSize: "0.88rem", color: "#6d28d9" }}>Adaptability Score</div>
                      <div className="track"><div className="fill adpt" style={{ width: adaptBarPct }} /></div>
                      <div className="score-box"><strong style={{ color: "#6d28d9" }}>{adapt > 0 ? `+${adapt}` : adapt}</strong></div>
                    </div>
                  </div>
                  <p className="small" style={{ textAlign: "center", marginTop: "1.5rem" }}>
                    Style scores reflect responses mapped to each mode (12 total). Adaptability ranges from −24 to +24.
                  </p>
                </div>

                <div className="callout" style={{ marginTop: "0.75rem", textAlign: "justify" }}>
                  <strong>Your Result:</strong> {resultText}
                </div>
              </div>

              {/* Adaptability Range — score, quote, user's tier only */}
              <div style={{ marginTop: '1.25rem', background: '#fff', border: '1.5px solid #e4e8f0', borderRadius: '14px', overflow: 'hidden' }}>
                {/* Score header */}
                <div style={{ padding: 'clamp(10px,2.5vw,20px)', background: '#f9fafb', borderBottom: '1px solid #e4e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 'clamp(2rem,7vw,48px)', fontWeight: 700, lineHeight: 1, color: '#0d0e10' }}>
                      {adapt > 0 ? `+${adapt}` : adapt}
                    </span>
                    <span style={{ fontSize: 'clamp(11px,1.8vw,13px)', color: '#4b5068' }}>Adaptability Score</span>
                  </div>
                  <span style={tierLabelStyle}>{currentTierRow.pill}</span>
                </div>
                {/* Quote */}
                <div style={{ padding: 'clamp(8px,1.8vw,14px) clamp(10px,2.5vw,20px)', borderBottom: '1px solid #e4e8f0' }}>
                  <div style={{ fontSize: 'clamp(11px,1.8vw,13px)', fontStyle: 'italic', fontWeight: 500, color: '#4b5068', lineHeight: 1.6, borderLeft: '3px solid #A81026', paddingLeft: '14px' }}>
                    "The best leaders don't stay at the top or bottom of the ladder. They move up and down depending on what the person needs."
                  </div>
                </div>
                {/* User's tier row only */}
                <table className="adapt-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <colgroup><col /><col /></colgroup>
                  <thead>
                    <tr>
                      <th style={{ fontSize: '0.86rem', fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: '#4b5068', textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #e4e8f0', width: '190px' }}>
                        Score Range
                      </th>
                      <th style={{ fontSize: '0.86rem', fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: '#4b5068', textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #e4e8f0' }}>
                        What It Means
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: '#fffbeb' }}>
                      <td style={{ padding: '10px 14px', verticalAlign: 'top' }}>
                        <span style={{ display: 'inline-block', fontSize: '0.86rem', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: currentTierRow.pb, color: currentTierRow.pc, border: `1px solid ${currentTierRow.border}`, wordBreak: 'break-word' }}>
                          {currentTierRow.pill}
                        </span>
                        <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#d97706', marginTop: '4px' }}>← You are here</div>
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: '0.88rem', color: '#0d0e10', lineHeight: 1.6, verticalAlign: 'top' }}>
                        {currentTierRow.desc}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* ── PAGE 3 — HOW FLEXIBLY YOU LEAD ───────────────────────────── */}
          {/* <section className="ss-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">How Flexibly You Lead</h2>
                </div>
                <div className="pill red">Adaptability Score</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.5rem" }}>
                Your adaptability score reflects how fluently you move across all 5 styles based on what each situation and team member demands. The best leaders don't stay at one point on the leadership ladder — they read the person and the moment, and flex accordingly.
              </p> */}

          {/* AdaptCard */}
          {/* <div style={{ background: "#fff", border: "1.5px solid #e4e8f0", borderRadius: "16px", overflow: "hidden" }}> */}

          {/* Score header */}
          {/* <div style={{
                  padding: "20px", background: "#f9fafb",
                  borderBottom: "1px solid #e4e8f0",
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between", flexWrap: "wrap", gap: "10px",
                }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                    <span style={{ fontSize: "48px", fontWeight: 700, lineHeight: 1, color: "#0d0e10" }}>
                      {adapt > 0 ? `+${adapt}` : adapt}
                    </span>
                    <span style={{ fontSize: "13px", color: "#4b5068" }}>Adaptability Score</span>
                  </div>
                  <span style={tierLabelStyle}>{currentTierRow.pill}</span>
                </div> */}

          {/* Quote */}
          {/* <div style={{ padding: "14px 20px", borderBottom: "1px solid #e4e8f0" }}>
                  <div style={{
                    fontSize: "13px", fontStyle: "italic", fontWeight: 500,
                    color: "#4b5068", lineHeight: 1.6,
                    borderLeft: "3px solid #A81026", paddingLeft: "14px",
                  }}>
                    "The best leaders don't stay at the top or bottom of the ladder. They move up and down depending on what the person needs."
                  </div>
                </div> */}

          {/* Tier table */}
          {/* <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      <th style={{
                        fontSize: "9px", fontWeight: 800, letterSpacing: ".09em",
                        textTransform: "uppercase", color: "#4b5068",
                        textAlign: "left", padding: "10px 14px",
                        borderBottom: "1px solid #e4e8f0", width: "190px",
                      }}>
                        Score Range
                      </th>
                      <th style={{
                        fontSize: "9px", fontWeight: 800, letterSpacing: ".09em",
                        textTransform: "uppercase", color: "#4b5068",
                        textAlign: "left", padding: "10px 14px",
                        borderBottom: "1px solid #e4e8f0",
                      }}>
                        What It Means
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ADAPT_ROWS.map((row) => {
                      const isCurrentTier = currentTierKey === row.tier;
                      return (
                        <tr key={row.tier} style={{
                          background: isCurrentTier ? "#fffbeb" : (row.tier === "high" ? "#f9fafb" : "#fff"),
                        }}>
                          <td style={{ padding: "10px 14px", borderBottom: "1px solid #e4e8f0", verticalAlign: "top" }}>
                            <span style={{
                              display: "inline-block", fontSize: "10px", fontWeight: 700,
                              padding: "2px 8px", borderRadius: "999px",
                              background: row.pb, color: row.pc,
                              border: `1px solid ${row.border}`,
                              whiteSpace: "nowrap",
                            }}>
                              {row.pill}
                            </span>
                            {isCurrentTier && (
                              <div style={{ fontSize: "9px", fontWeight: 700, color: "#d97706", marginTop: "4px" }}>
                                ← You are here
                              </div>
                            )}
                          </td>
                          <td style={{
                            padding: "10px 14px", borderBottom: "1px solid #e4e8f0",
                            fontSize: "12px", color: "#0d0e10", lineHeight: 1.6, verticalAlign: "top",
                            textAlign: "justify",
                          }}>
                            {row.desc}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section> */}

          {/* ── PAGE 3 — STYLE DEFINITIONS (Signaling & Stimulating) ──────── */}
          <section className="ss-page">
            <div className="page-inner" style={{
              paddingTop: "2.5rem",
              paddingRight: "2.5rem",
              paddingBottom: "0",
              paddingLeft: "1.25rem",
            }}>
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Leadership Style Definitions</h2>
                </div>
                <div className="pill red">Signaling &amp; Stimulating</div>
              </div>
              <div className="card" style={{ marginBottom: "1.5rem" }}>
                <h3>5S Parameters: Conceptual Definitions Across Roles and Levels</h3>
                <p className="lead">
                  The 5S Leadership framework describes how individuals naturally tend to influence, motivate, and guide those around them. Rather than assuming one style fits all situations, it acknowledges that effective leaders draw on different modes — and that awareness of your dominant style is the first step toward expanding your range.
                </p>
              </div>
              {STYLE_DEFS_SORTED.slice(0, 2).map(renderStyleCard)}
            </div>
          </section>

{/* //////////////////////////////////////////////////// */}
          {/* ── PAGE 4— STYLE DEFINITIONS (Suggesting & Sharing & Sanctioning) */}
          {/* ////////////////////////////////////////////////////////////////////////// */}
          <section className="ss-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Leadership Style Definitions</h2>
                </div>
                <div className="pill red" style={{ background: "#f5f3ff", color: "#6d28d9" }}>
                  Suggesting &amp; Sharing &amp; Sanctioning
                </div>
              </div>
              {STYLE_DEFS_SORTED.slice(2, 4).map(renderStyleCard)}
            </div>
          </section>

          {/* ── PAGE 6 — BACK COVER ───────────────────────────────────────── */}
          <section className="ss-page back-cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
              minHeight: "1556px",
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "1.5rem 2.5rem",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>Adaptability is the Edge</h2>
                    <p>
                      While you naturally lean toward a {dominantStyle} leadership style, the most effective leaders learn to flex across all four modes — reading what the situation, the team, and the moment requires. Use these insights to recognise the preferred styles of those you lead and adapt your approach to create deeper engagement, stronger trust, and better results.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Practical Leadership Design</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>
                      For this participant, the most effective leadership development path involves building on the {dominantStyle} foundation while deliberately practising the complementary styles in low-risk settings. Work with a coach or peer to identify situations where a different style would yield a better outcome — then experiment intentionally. Over time, expanding your style repertoire creates a measurably more versatile and impactful leadership presence.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report Type</span><strong>5S Leadership Style Report</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the 5S Leadership Influence Style Survey framework.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}

// import React, { useEffect, useRef } from "react";
// import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// // ── Theme tokens (replicate VAK T object; #A80D2C → #A81026) ─────────────────
// const T = {
//   navy: "#A81026",
//   blue: "#C4163A",
//   teal: "#7B0F2B",
//   gold: "#D79A1E",
//   slate: "#5B677A",
//   bg: "#F5F8FC",
//   card: "#FFFFFF",
//   line: "#DCE5EF",
//   ink: "#1D2733",
// };

// // ── 5S style colour tokens ────────────────────────────────────────────────────
// const STYLE_COLORS = {
//   Signaling:           { color: "#3b5ec0", bg: "#f0f4ff", fillStart: "#1e3a8a", fillEnd: "#3b82f6" },
//   Stimulating:         { color: "#16a34a", bg: "#f0fdf4", fillStart: "#14532d", fillEnd: "#22c55e" },
//   Suggesting:          { color: "#d9790a", bg: "#fffbeb", fillStart: "#b45309", fillEnd: "#f59e0b" },
//   Sharing_Sanctioning: { color: "#7c3aed", bg: "#faf5ff", fillStart: "#4c1d95", fillEnd: "#a78bfa" },
//   Total_Adaptability:  { color: "#6d28d9", bg: "#f8f0ff", fillStart: "#4c1d95", fillEnd: "#c4b5fd" },
// };

// // ── Style definition content ──────────────────────────────────────────────────
// const styleDefs = [
//   {
//     key: "signaling",
//     color: STYLE_COLORS.Signaling.color,
//     bg: STYLE_COLORS.Signaling.bg,
//     emoji: "📡",
//     label: "Signaling — Leadership Style",
//     conceptual:
//       "Leaders who score high on Signaling are highly attuned to context and environment. They read the room, pick up on unspoken cues, and communicate through visible actions, symbols, and presence as much as through words. Their leadership is characterized by deliberate messaging and purposeful visibility.",
//     cognitiveTitle: "Cognitive Characteristics",
//     cognitive: [
//       "Process their environment holistically, noticing patterns, energy shifts, and unspoken dynamics before others articulate them.",
//       "Prefer to lead by example and demonstration, believing that actions carry more weight than instructions.",
//       "Tend to think in frameworks and models, naturally translating complex ideas into clear signals and cues for their teams.",
//       "Are highly sensitive to alignment between what is said and what is done, and respond strongly to inconsistency.",
//     ],
//     behaviorTitle: "Behavioral Indicators",
//     behavior: [
//       "Communicate organizational priorities through visible behaviors — how they spend time, who they recognize, and what they choose to address publicly.",
//       "Often set the cultural tone of a team through consistent, deliberate non-verbal and symbolic cues.",
//       "In meetings and forums, listen intently before speaking; when they do speak, their words carry disproportionate weight.",
//       "Natural at translating strategy into observable behaviors that the team can model and follow.",
//       "Use phrases like \"let me show you,\" \"watch how this is done,\" or \"the signal we need to send is...\"",
//     ],
//   },
//   {
//     key: "stimulating",
//     color: STYLE_COLORS.Stimulating.color,
//     bg: STYLE_COLORS.Stimulating.bg,
//     emoji: "⚡",
//     label: "Stimulating — Leadership Style",
//     conceptual:
//       "Leaders with a Stimulating style are energizers. They create intellectual and emotional energy in the people around them, challenge assumptions, and build cultures of curiosity and ambition. Their presence raises the tempo and aspiration of those they lead.",
//     cognitiveTitle: "Cognitive Characteristics",
//     cognitive: [
//       "Naturally drawn to complexity, challenge, and unexplored possibility — they are energized by problems that others find daunting.",
//       "Think expansively, often connecting seemingly unrelated ideas to surface new approaches or opportunities.",
//       "Process information quickly and enjoy dialogue as a mode of thinking — they refine ideas through debate and discussion.",
//       "High need for intellectual stimulation themselves; they disengage from routine and seek environments that require creative problem-solving.",
//     ],
//     behaviorTitle: "Behavioral Indicators",
//     behavior: [
//       "Regularly ask provocative or stretch questions designed to expand thinking: \"What would it look like if we doubled this?\" or \"Why can't we?\"",
//       "Create forums and conversations where ideas are challenged respectfully, and where bold thinking is rewarded.",
//       "Coach by asking more than telling; they prefer to stimulate insight rather than provide answers directly.",
//       "Attract high-potential talent because they create environments where growth is expected and boredom is rare.",
//       "Use expressions like \"what if,\" \"push further,\" or \"let's think about this differently.\"",
//     ],
//   },
//   {
//     key: "suggesting",
//     color: STYLE_COLORS.Suggesting.color,
//     bg: STYLE_COLORS.Suggesting.bg,
//     emoji: "💡",
//     label: "Suggesting — Leadership Style",
//     conceptual:
//       "Leaders with a Suggesting style are influential advisors. They build their authority through insight, perspective, and trusted counsel rather than directive authority. They excel at planting ideas, shaping thinking, and guiding others toward well-reasoned conclusions.",
//     cognitiveTitle: "Cognitive Characteristics",
//     cognitive: [
//       "Prefer to influence through well-timed, well-framed recommendations rather than mandates or instructions.",
//       "Strong analytical thinkers who invest time in understanding context, history, and stakeholder perspectives before offering input.",
//       "Tend to be patient communicators; they allow ideas to develop through conversation and are comfortable with ambiguity.",
//       "Often the most valued voice in the room because they speak less but observe more, making their contributions highly credible.",
//     ],
//     behaviorTitle: "Behavioral Indicators",
//     behavior: [
//       "Frequently act as sounding boards and thought partners; colleagues and senior leaders often seek them out for perspective.",
//       "Frame recommendations indirectly, inviting others to arrive at conclusions rather than issuing directives.",
//       "Skilled at reading stakeholders and timing their input for maximum receptiveness.",
//       "Build influence quietly over time; their impact is felt through the quality of decisions that teams make after consulting them.",
//       "Common phrases include \"have you considered,\" \"one option might be,\" or \"what I'd reflect on is...\"",
//     ],
//   },
//   {
//     key: "sharing_sanctioning",
//     color: STYLE_COLORS.Sharing_Sanctioning.color,
//     bg: STYLE_COLORS.Sharing_Sanctioning.bg,
//     emoji: "🤝",
//     label: "Sharing & Sanctioning — Leadership Style",
//     conceptual:
//       "Leaders with a Sharing & Sanctioning style are builders of shared ownership and collective accountability. They are highly effective at creating alignment, distributing authority, and ensuring that standards and expectations are held clearly across the team.",
//     cognitiveTitle: "Cognitive Characteristics",
//     cognitive: [
//       "Value collective wisdom and shared decision-making; they believe that outcomes improve when more perspectives are incorporated.",
//       "Strong at designing processes, structures, and rituals that embed shared norms and expectations across the team.",
//       "Highly attuned to fairness, equity, and consistency — they monitor whether everyone is operating to the same standard.",
//       "Process leadership challenges through consultation and consensus-building before arriving at a position.",
//     ],
//     behaviorTitle: "Behavioral Indicators",
//     behavior: [
//       "Actively distribute ownership and accountability, ensuring team members understand their authority and are held to clear expectations.",
//       "Create transparent frameworks for decision-making so that teams understand how and why choices are made.",
//       "Follow up on commitments consistently and hold team members — and themselves — accountable to agreed standards.",
//       "Build highly cohesive teams because they invest in shared values, rituals, and common language.",
//       "Use phrases like \"we own this together,\" \"what are the standards we've agreed on,\" or \"let's make this decision as a team.\"",
//     ],
//   },
// ];

// /**
//  * Hidden5SReport
//  * ──────────────
//  * Renders the 5S Leadership Style report offscreen, captures each .ss-page with
//  * html2canvas, stitches into a multi-page A4 PDF via jsPDF, then saves it.
//  *
//  * Props:
//  *   participantData  – null | { name, email, phone, submission_time, scores }
//  *   onComplete       – () => void
//  */
// export default function Hidden5SReport({ participantData, onComplete }) {
//   const containerRef = useRef(null);

//   // ── Load html2canvas + jsPDF once ──────────────────────────────────────────
//   useEffect(() => {
//     let pending = 2;
//     const done = () => { pending--; };
//     const load = (src, id) => {
//       if (document.getElementById(id)) { done(); return; }
//       const s = document.createElement("script");
//       s.id = id; s.src = src;
//       s.onload = done; s.onerror = done;
//       document.head.appendChild(s);
//     };
//     load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-5s-v1");
//     load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js",        "jspdf-5s-v1");
//   }, []);

//   // ── Generate PDF whenever participantData is set ────────────────────────────
//   useEffect(() => {
//     if (!participantData) return;

//     const generate = async () => {
//       // Wait for both scripts to be ready
//       await new Promise((res) => {
//         const check = () => {
//           if (window.html2canvas && window.jspdf?.jsPDF) { res(); return; }
//           setTimeout(check, 100);
//         };
//         check();
//       });

//       // Allow DOM to fully paint
//       await new Promise((r) => setTimeout(r, 1000));

//       const el = containerRef.current;
//       if (!el) { onComplete?.(); return; }

//       try {
//         const { jsPDF } = window.jspdf;
//         const A4_W = 210;
//         const A4_H = 297;

//         const pageEls = Array.from(el.querySelectorAll(".ss-page"));
//         const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

//         for (let i = 0; i < pageEls.length; i++) {
//           const canvas = await window.html2canvas(pageEls[i], {
//             scale: 2,
//             useCORS: true,
//             allowTaint: true,
//             logging: false,
//             backgroundColor: "#ffffff",
//             width: pageEls[i].scrollWidth,
//             height: pageEls[i].scrollHeight,
//             windowWidth: pageEls[i].scrollWidth,
//           });

//           const imgData  = canvas.toDataURL("image/jpeg", 0.95);
//           const logicalW = canvas.width  / 2;
//           const logicalH = canvas.height / 2;
//           const MM_PER_PX   = 25.4 / 96;
//           const naturalW    = logicalW * MM_PER_PX;
//           const naturalH    = logicalH * MM_PER_PX;
//           const scaleToW    = A4_W / naturalW;
//           const projectedH  = naturalH * scaleToW;

//           let imgW, imgH, xOff, yOff;
//           if (projectedH <= A4_H) {
//             imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
//           } else {
//             const scaleToH = A4_H / naturalH;
//             imgW = naturalW * scaleToH; imgH = A4_H;
//             xOff = (A4_W - imgW) / 2;  yOff = 0;
//           }

//           if (i > 0) pdf.addPage();
//           pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
//         }

//         const safeName = (participantData.name || "Report").replace(/\s+/g, "_");
//         pdf.save(`5S_Report_${safeName}.pdf`);
//       } catch (err) {
//         console.error("5S PDF generation failed", err);
//       } finally {
//         onComplete?.();
//       }
//     };

//     generate();
//   }, [participantData]);

//   if (!participantData) return null;

//   const { name, email, phone, submission_time, scores = {} } = participantData;

//   const sig   = scores.Signaling           || 0;
//   const stim  = scores.Stimulating         || 0;
//   const sug   = scores.Suggesting          || 0;
//   const shar  = scores.Sharing_Sanctioning || 0;
//   const adapt = scores.Total_Adaptability  || 0;

//   const total = (sig + stim + sug + shar) || 12;

//   const sigPct  = ((sig  / total) * 100).toFixed(1) + "%";
//   const stimPct = ((stim / total) * 100).toFixed(1) + "%";
//   const sugPct  = ((sug  / total) * 100).toFixed(1) + "%";
//   const sharPct = ((shar / total) * 100).toFixed(1) + "%";

//   // Adaptability: range -24 to +24; display as % of full range for the bar
//   const adaptBarPct = (((adapt + 24) / 48) * 100).toFixed(1) + "%";

//   // Dominant style label
//   const styleKeys = [
//     { key: "Signaling",           val: sig,  label: "Signaling"            },
//     { key: "Stimulating",         val: stim, label: "Stimulating"          },
//     { key: "Suggesting",          val: sug,  label: "Suggesting"           },
//     { key: "Sharing_Sanctioning", val: shar, label: "Sharing & Sanctioning"},
//   ];
//   const dominantStyle = styleKeys.reduce((a, b) => (b.val > a.val ? b : a)).label;

//   const dateStr = submission_time
//     ? new Date(submission_time).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
//     : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

//   const resultText =
//     `Based on your responses, your primary leadership influence style is ${dominantStyle}. ` +
//     (dominantStyle === "Signaling"
//       ? "You lead through presence, action, and deliberate communication — your team reads your cues and takes direction from what you visibly prioritize and model."
//       : dominantStyle === "Stimulating"
//       ? "You lead by raising energy, ambition, and intellectual challenge — your natural mode is to energize and stretch the people around you toward greater performance."
//       : dominantStyle === "Suggesting"
//       ? "You lead through trusted insight and well-timed counsel — your influence is built on the quality of your perspective and your skill in guiding others toward sound conclusions."
//       : "You lead through shared ownership and collective accountability — your strength is in aligning people around clear expectations and ensuring that standards are held consistently.");

//   const adaptabilityText =
//     adapt >= 10
//       ? "Your adaptability score indicates a highly flexible leadership approach. You read context well and are able to adjust your style fluidly to meet the needs of different people and situations."
//       : adapt >= 0
//       ? "Your adaptability score indicates a moderately flexible approach. You maintain a consistent leadership presence while being willing to adjust when the situation requires it."
//       : "Your adaptability score suggests a preference for a consistent, predictable leadership approach. While this brings stability, developing greater style flexibility could expand your effectiveness across a broader range of situations.";

//   const renderStyleCard = (style) => (
//     <div
//       key={style.key}
//       className="ss-item"
//       style={{ borderLeft: `4px solid ${style.color}`, paddingLeft: "1rem", marginBottom: "1.5rem" }}
//     >
//       <strong style={{ display: "block", color: style.color, fontSize: "1.15rem", marginBottom: "0.5rem" }}>
//         {style.emoji} {style.label}
//       </strong>
//       <p style={{ margin: "0 0 0.75rem", color: "#334155", fontSize: "0.92rem" }}>
//         <strong>Conceptual Definition:</strong> {style.conceptual}
//       </p>
//       <p style={{ margin: "0 0 0.35rem", fontWeight: 700, fontSize: "0.9rem", color: "#0f2744" }}>
//         {style.cognitiveTitle}
//       </p>
//       <ul style={{ margin: "0 0 0.75rem 0", paddingLeft: "1.25rem", fontSize: "0.88rem", color: "#475569" }}>
//         {style.cognitive.map((pt, i) => <li key={i} style={{ marginBottom: "0.3rem" }}>{pt}</li>)}
//       </ul>
//       <p style={{ margin: "0 0 0.35rem", fontWeight: 700, fontSize: "0.9rem", color: "#0f2744" }}>
//         {style.behaviorTitle}
//       </p>
//       <ul style={{ margin: "0 0 0.75rem 0", paddingLeft: "1.25rem", fontSize: "0.88rem", color: "#475569" }}>
//         {style.behavior.map((pt, i) => <li key={i} style={{ marginBottom: "0.3rem" }}>{pt}</li>)}
//       </ul>
//     </div>
//   );

//   return (
//     <div
//       ref={containerRef}
//       aria-hidden="true"
//       style={{
//         position:      "fixed",
//         top:           "0",
//         left:          "-9999px",
//         width:         "1100px",
//         opacity:       0,
//         pointerEvents: "none",
//         zIndex:        -1,
//       }}
//     >
//       <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />

//       <style>{`
//         .ss-pdf-root {
//           font-family: 'Satoshi', sans-serif;
//           background: linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
//           color: #1f2937;
//           line-height: 1.5;
//         }
//         .ss-pdf-root .report { max-width: 1100px; margin: 0 auto; padding: 2rem 1rem 4rem; }
//         .ss-pdf-root .ss-page {
//           background: #ffffff; border: 1px solid #d9e0e7; border-radius: 28px;
//           box-shadow: 0 18px 40px rgba(14,39,68,.10); overflow: hidden; margin-bottom: 2rem;
//         }

//         /* Cover */
//         .ss-pdf-root .cover { min-height:1556px; position:relative; color:white; }
//         .ss-pdf-root .cover-inner { padding:3.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:1556px; }
//         .ss-pdf-root .brand { display:flex; align-items:center; justify-content:space-between; gap:1rem; position:relative; z-index: 2; }
//         .ss-pdf-root .badge { border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1); padding:.5rem 1rem; border-radius:999px; font-size:0.76rem; }

//         .ss-pdf-root .logo-panel {
//           position: absolute; top: 0; right: 0;
//           width: 500px; height: 250px;
//           display: flex; align-items: center; justify-content: center; z-index: 1;
//         }
//         .ss-pdf-root .logo-panel-inner {
//           display: flex; flex-direction: column; align-items: center;
//           position: relative; z-index: 2; padding-left: 60px; padding-bottom: 25px;
//         }
//         .ss-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

//         .ss-pdf-root .cover-title h2 { margin:0 0 1rem; font-size:clamp(2.4rem,4.5vw,4rem); line-height:.95; color:#FFFFFF; }
//         .ss-pdf-root .cover-title p  { font-size:1.2rem; color:rgba(255,255,255,.82); max-width:100%; }
//         .ss-pdf-root .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem; }
//         .ss-pdf-root .meta { background:rgba(255,255,255,.1); font-size:1.2rem; border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem; }
//         .ss-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
//         .ss-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
//         .ss-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem; }
//         .ss-pdf-root .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
//         .ss-pdf-root .footer-note { display:flex; justify-content:space-between; gap:1rem; color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:1rem; }

//         /* Inner pages */
//         .ss-pdf-root .page-inner { padding: 2.5rem; }
//         .ss-pdf-root .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin-bottom:2rem; }
//         .ss-pdf-root .section-kicker { color:#A81026; font-size:0.8rem; text-transform:uppercase; letter-spacing:.12em; font-weight:700; }
//         .ss-pdf-root .section-title { margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; }
//         .ss-pdf-root .summary-grid { display:grid; grid-template-columns:1fr; gap:1rem; }
//         .ss-pdf-root .card { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid #d9e0e7; border-radius:20px; padding:1.5rem; }
//         .ss-pdf-root .card h3 { margin:0 0 .75rem; font-size:1.2rem; color:#0f2744; }
//         .ss-pdf-root .lead { color:#334155; font-size:1rem; margin:0; }
//         .ss-pdf-root .pill { display:inline-flex; align-items:center; gap:.45rem; padding:.5rem .85rem; border-radius:999px; font-size:0.8rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; }
//         .ss-pdf-root .pill.red { background:#fef2f2; color:#A81026; }

//         /* Bars */
//         .ss-pdf-root .bars { display:grid; gap:1.2rem; margin-top:1rem; }
//         .ss-pdf-root .bar-row { display:grid; grid-template-columns:165px 1fr 60px; gap:1rem; align-items:center; }
//         .ss-pdf-root .bar-label { font-size:0.95rem; font-weight:700; color:#0f2744; }
//         .ss-pdf-root .track { height:24px; background:#e9eef3; border-radius:999px; overflow:hidden; position:relative; }
//         .ss-pdf-root .fill { height:100%; border-radius:999px; position:relative; }
//         .ss-pdf-root .fill.sig  { background:linear-gradient(90deg,#1e3a8a,#3b82f6); }
//         .ss-pdf-root .fill.stim { background:linear-gradient(90deg,#14532d,#22c55e); }
//         .ss-pdf-root .fill.sug  { background:linear-gradient(90deg,#b45309,#f59e0b); }
//         .ss-pdf-root .fill.shar { background:linear-gradient(90deg,#4c1d95,#a78bfa); }
//         .ss-pdf-root .fill.adpt { background:linear-gradient(90deg,#4c1d95,#c4b5fd); }
//         .ss-pdf-root .score-box { text-align:right; font-variant-numeric:tabular-nums; }
//         .ss-pdf-root .score-box strong { display:block; color:#0f2744; font-size:1.1rem; }

//         /* Callout & List */
//         .ss-pdf-root .callout { background:linear-gradient(135deg,#fff7f7,#fbeaea); border:1px solid #d9e0e7; border-radius:18px; padding:1.25rem 1.5rem; color:#334155; }
//         .ss-pdf-root .ss-list { display:grid; gap:.9rem; }
//         .ss-pdf-root .ss-item { border:1px solid #d9e0e7; border-radius:16px; padding:1.25rem; background:#fff; }
//         .ss-pdf-root .ss-item p { margin:0.5rem 0 0; color:#475569; font-size:0.9rem; }

//         /* Back cover */
//         .ss-pdf-root .back-cover { min-height:1556px; color:white; }
//         .ss-pdf-root .back-cover .page-inner { min-height:1556px; display:flex; flex-direction:column; justify-content:space-between; }
//         .ss-pdf-root .closing h2 { margin:0 0 .85rem; font-size:clamp(1.85rem,3.5vw,2.8rem); color:#FFFFFF; }
//         .ss-pdf-root .closing p  { text-align:justify; max-width:100%; color:rgba(255,255,255,.82); font-size:1.2rem; }
//         .ss-pdf-root .contact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem; }
//         .ss-pdf-root .contact { background:rgba(168,16,38,.1); border:1px solid rgba(255,255,255,.16); border-radius:16px; padding:.9rem; }
//         .ss-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
//         .ss-pdf-root .contact strong { font-size:0.92rem; }
//         .ss-pdf-root .small { font-size:0.73rem; color:#5B677A; }
//         .ss-pdf-root .light { color:rgba(255,255,255,.66); }
//       `}</style>

//       <div className="ss-pdf-root">
//         <main className="report">

//           {/* ── PAGE 1 — COVER ────────────────────────────────────────────── */}
//           <section className="ss-page cover">
//             <div
//               style={{
//                 background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
//                 minHeight: "1556px",
//                 flexDirection: "column",
//               }}
//             >
//               {/* Logo panel — identical SVG shape to VAK */}
//               <div className="logo-panel">
//                 <svg
//                   width="100%" height="100%"
//                   viewBox="0 0 500 250"
//                   style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
//                   preserveAspectRatio="none"
//                 >
//                   <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
//                   <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
//                   <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
//                 </svg>
//                 <div className="logo-panel-inner">
//                   <img
//                     src={Acumen_25_Years_LOGO}
//                     alt="Acumen Logo"
//                     className="logo-img"
//                   />
//                 </div>
//               </div>

//               <div className="cover-inner">
//                 <div className="brand">
//                   <div className="badge">Confidential Individual Report</div>
//                 </div>

//                 <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: "relative", zIndex: 2 }}>
//                   <div className="cover-title">
//                     <h2>5S Leadership Style Assessment</h2>
//                     <p>
//                       This comprehensive assessment report presents an analysis of your leadership influence preferences using the 5S framework, helping identify how you most naturally lead, motivate, and guide others — and how adaptable your style is across different contexts and situations.
//                     </p>
//                   </div>

//                   <div className="cover-grid">
//                     <div className="meta"><span>Participant</span><strong>{name || "—"}</strong></div>
//                     <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
//                     <div className="meta"><span>Primary Style</span><strong>{dominantStyle}</strong></div>
//                   </div>

//                   <div className="cover-badges">
//                     {["Assessment Summary", "5S Distribution", "Adaptability Score", "Style Implications"].map((b) => (
//                       <span key={b} className="cover-badge">{b}</span>
//                     ))}
//                   </div>
//                 </div>

//                 <div className="footer-note" style={{ position: "relative", zIndex: 2 }}>
//                   <div>Based on the 5S Leadership Influence Style Survey framework.</div>
//                   <div>12 questions distributed across four leadership modes.</div>
//                 </div>
//               </div>
//             </div>
//           </section>

//           {/* ── PAGE 2 — ASSESSMENT SUMMARY ───────────────────────────────── */}
//           <section className="ss-page">
//             <div className="page-inner">
//               <div className="topbar">
//                 <div>
//                   <div className="section-kicker">Page 2</div>
//                   <h2 className="section-title">Assessment Summary</h2>
//                 </div>
//                 <div className="pill red">Dominant Style: {dominantStyle}</div>
//               </div>

//               <div className="summary-grid">
//                 <div className="card">
//                   <h3>Understanding Your Leadership Style</h3>
//                   <p className="lead">
//                     While each leader has a primary mode through which they most naturally influence others, effective leadership requires the ability to flex across all four styles. The 5S framework identifies not just your dominant mode but your overall adaptability — the degree to which you adjust your approach based on context, audience, and situation.
//                   </p>
//                   <p className="lead" style={{ marginTop: "1rem" }}>
//                     The survey you completed was designed to surface your predominant leadership influence preference. These scores reveal your natural style, providing actionable insight into how you build rapport, motivate others, and can deepen your effectiveness by recognizing and matching the preferred styles of the people you lead.
//                   </p>
//                 </div>

//                 <div className="card">
//                   <h3>Why Style Awareness Matters</h3>
//                   <p className="lead">
//                     Research consistently shows that leaders who understand their own influence style — and can consciously adapt it — achieve stronger engagement, better communication outcomes, and higher team performance. Style awareness is not about changing who you are; it is about expanding your range so you can meet people where they are and lead more effectively in diverse contexts.
//                   </p>
//                 </div>

//                 <div className="card">
//                   <h3>Your 5S Distribution</h3>
//                   <div className="bars">
//                     <div className="bar-row">
//                       <div className="bar-label">Signaling</div>
//                       <div className="track"><div className="fill sig" style={{ width: sigPct }}></div></div>
//                       <div className="score-box"><strong>{sig}</strong></div>
//                     </div>
//                     <div className="bar-row">
//                       <div className="bar-label">Stimulating</div>
//                       <div className="track"><div className="fill stim" style={{ width: stimPct }}></div></div>
//                       <div className="score-box"><strong>{stim}</strong></div>
//                     </div>
//                     <div className="bar-row">
//                       <div className="bar-label">Suggesting</div>
//                       <div className="track"><div className="fill sug" style={{ width: sugPct }}></div></div>
//                       <div className="score-box"><strong>{sug}</strong></div>
//                     </div>
//                     <div className="bar-row">
//                       <div className="bar-label">Sharing &amp; Sanctioning</div>
//                       <div className="track"><div className="fill shar" style={{ width: sharPct }}></div></div>
//                       <div className="score-box"><strong>{shar}</strong></div>
//                     </div>
//                     <div className="bar-row" style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px dashed #e2e8f0" }}>
//                       <div className="bar-label" style={{ fontSize: "0.88rem", color: "#6d28d9" }}>Adaptability Score</div>
//                       <div className="track"><div className="fill adpt" style={{ width: adaptBarPct }}></div></div>
//                       <div className="score-box"><strong style={{ color: "#6d28d9" }}>{adapt > 0 ? `+${adapt}` : adapt}</strong></div>
//                     </div>
//                   </div>
//                   <p className="small" style={{ textAlign: "center", marginTop: "1.5rem" }}>
//                     Style scores represent question responses mapped to each mode (12 total). Adaptability ranges from −24 to +24.
//                   </p>
//                 </div>

//                 <div className="callout" style={{ marginTop: "0.75rem" }}>
//                   <strong>Your Result:</strong> {resultText}
//                 </div>

//                 <div className="callout" style={{ marginTop: "0.5rem", background: "linear-gradient(135deg,#f5f3ff,#ede9fe)", borderColor: "#ddd6fe" }}>
//                   <strong style={{ color: "#6d28d9" }}>Adaptability:</strong>{" "}
//                   <span style={{ color: "#334155" }}>{adaptabilityText}</span>
//                 </div>
//               </div>
//             </div>
//           </section>

//           {/* ── PAGE 3 — STYLE DEFINITIONS (Signaling & Stimulating) ──────── */}
//           <section className="ss-page">
//             <div className="page-inner">
//               <div className="topbar">
//                 <div>
//                   <div className="section-kicker">Page 3</div>
//                   <h2 className="section-title">Leadership Style Definitions</h2>
//                 </div>
//                 <div className="pill red">Signaling &amp; Stimulating</div>
//               </div>
//               <div className="card" style={{ marginBottom: "1.5rem" }}>
//                 <h3>5S Parameters: Conceptual Definitions Across Roles and Levels</h3>
//                 <p className="lead">
//                   The 5S Leadership framework describes how individuals naturally tend to influence, motivate, and guide those around them. Rather than assuming one style fits all situations, it acknowledges that effective leaders draw on different modes — and that awareness of your dominant style is the first step toward expanding your range.
//                 </p>
//               </div>
//               <div className="ss-list">
//                 {styleDefs.slice(0, 2).map(renderStyleCard)}
//               </div>
//             </div>
//           </section>

//           {/* ── PAGE 4 — STYLE DEFINITIONS (Suggesting & Sharing & Sanctioning) */}
//           <section className="ss-page">
//             <div className="page-inner">
//               <div className="topbar">
//                 <div>
//                   <div className="section-kicker">Page 4</div>
//                   <h2 className="section-title">Leadership Style Definitions</h2>
//                 </div>
//                 <div className="pill red" style={{ background: "#f5f3ff", color: "#6d28d9" }}>
//                   Suggesting &amp; Sharing &amp; Sanctioning
//                 </div>
//               </div>
//               <div className="ss-list">
//                 {styleDefs.slice(2, 4).map(renderStyleCard)}
//               </div>
//             </div>
//           </section>

//           {/* ── PAGE 5 — BACK COVER ───────────────────────────────────────── */}
//           <section className="ss-page back-cover">
//             <div
//               style={{
//                 background: `linear-gradient(135deg,#102741,#183b62 60%,#224f7b)`,
//                 minHeight: "1556px",
//               }}
//             >
//               <div style={{
//                 display: "flex", justifyContent: "space-between", alignItems: "center",
//                 padding: "1.5rem 2.5rem",
//                 borderBottom: "1px solid rgba(255,255,255,0.15)",
//               }}>
//                 <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
//                   Back Cover
//                 </div>
//                 <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
//                   Acumen Business Catalyst
//                 </div>
//               </div>

//               <div className="page-inner">
//                 <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
//                   <div className="closing">
//                     <h2>Adaptability is the Edge</h2>
//                     <p>
//                       While you naturally lean toward a {dominantStyle} leadership style, the most effective leaders learn to flex across all four modes — reading what the situation, the team, and the moment requires. Use these insights to recognize the preferred styles of those you lead and adapt your approach to create deeper engagement, stronger trust, and better results.
//                     </p>
//                   </div>
//                 </div>

//                 <div>
//                   <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
//                     <h3 style={{ color: "white" }}>Practical Leadership Design</h3>
//                     <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>
//                       For this participant, the most effective leadership development path involves building on the {dominantStyle} foundation while deliberately practicing the complementary styles in low-risk settings. Work with a coach or peer to identify situations where a different style would yield a better outcome — then experiment intentionally. Over time, expanding your style repertoire creates a measurably more versatile and impactful leadership presence.
//                     </p>
//                   </div>

//                   <div className="contact-grid">
//                     <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
//                     <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
//                     <div className="contact"><span>Report Type</span><strong>5S Leadership Style Report</strong></div>
//                   </div>

//                   <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
//                     This report is generated from the 5S Leadership Influence Style Survey framework.
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </section>

//         </main>
//       </div>
//     </div>
//   );
// }